// Figma: "Loyalty Cloud" file, node 18777:3043 ("Table" — Reports tab) — search+filter
// header, Name/Weekly Subscription/Monthly Subscription columns with checkbox toggles, and
// an icon-only download action per row. Built on ../components/DataTableShell.js (same
// table shell used by the Today dashboard) for the header/search/pagination chrome; the
// subscription cells and action column are overridden below via renderCell to match the
// checkbox + download-icon look instead of the shell's generic cell rendering.
// LOGIC PORTED from LC2's ReportsTable.js (commons/components/HomeTabs/Today/Reports/
// ReportsTable.js): same three static reports (all-time cash-back transactions, claimed
// cash-back, claimed cash-back summary), same `sendXReport`/`subscribeReport` mutations
// (community-scoped via `building_sfid` when a community is selected, else
// `account_sfid`), same weekly/monthly subscription toggles read off
// `getTeamMemberReportSubscriptionList`.
// Gate ported verbatim from LC2's TodayTab.js (commons/components/HomeTabs/Today/
// TodayTab.js): only `isStakeAdmin` or (the *route's* account_sfid — `params.account_sfid`,
// not the logged-in user's own account — is one of ['0014S000004aYHhQAM',
// '0014S000004bx6LQAQ'] && userRole in ['Administrator', 'Regional Property Manager']) may
// see this tab — everyone else gets a "not available" message instead of the raw table
// (see DashboardPage.js's `reports` branch). The task brief for this port paraphrased this
// as a single sfid; the actual LC2 source checks two, both kept here.
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {Box, ButtonBase, Checkbox, Typography} from '@mui/material';

import {tokens} from '../../../themes/ThemeCustomization';
import {useLazyGetTeamMemberInfoQuery, useSubscribeReportMutation} from '../../../features/loyaltyCloudApi/apiSlice';
import {
    useSendAllTimeCashBackTransactionsReportMutation,
    useSendClaimedCashBackReportMutation,
    useSendPropertySummaryReportMutation
} from '../../../features/coreApi/apiSlice';
import {getAccountSfid, getEmail, getIsStakeAdmin, getTeamMemberId, getTeamMemberReportSubscriptionList, getTeamMemberRole} from '../../../app/store/reducers/session';
import DataTableShell from '../components/DataTableShell';
import AuthToast from '../../authentication/components/AuthToast';

import downloadIcon from '../../../assets/shell/invoice-download-icon.svg';

const REPORTS_ADMIN_ACCOUNT_SFIDS = ['0014S000004aYHhQAM', '0014S000004bx6LQAQ'];
const REPORTS_ADMIN_ROLES = ['Administrator', 'Regional Property Manager'];

const REPORT_DEFS = [
    {report_id: 'all_time_cash_back_transaction_report', report_name: 'All time Cash Back transaction export'},
    {report_id: 'claimed_cash_back_report', report_name: 'Claimed Cash Back export'},
    {report_id: 'property_summary_report', report_name: 'Claimed Cash Back summary report'}
];

export const canViewReports = (isStakeAdmin, accountSfid, userRole) =>
    isStakeAdmin || (REPORTS_ADMIN_ACCOUNT_SFIDS.includes(accountSfid) && REPORTS_ADMIN_ROLES.includes(userRole));

const ReportsSection = () => {
    const params = useParams();
    const communitySfid = params?.community_sfid;
    const accountSfid = params?.account_sfid;

    const userEmail = useSelector(getEmail);
    const userAccountSfid = useSelector(getAccountSfid);
    const userAccountId = useSelector(getTeamMemberId);
    const allReportSubscriptions = useSelector(getTeamMemberReportSubscriptionList);
    // session_info returns subscriptions for every account the user has set up; each entry
    // carries its own account_sfid, so only keep the ones for the account chosen in the dropdown.
    // The store copy is also rewritten by the unscoped session_info refetches fired from
    // MainLayout/Header whenever the Session tag is invalidated, which would clobber this
    // account's subscriptions right after a toggle. So this component keeps its own copy of
    // the account-scoped response (`scopedSubscriptions`) and prefers it over the store.
    // Tagged with the account it was fetched for, so a response that lands after the dropdown
    // has moved on to another account is never shown for the new one.
    const [scoped, setScoped] = useState({sfid: null, list: null});
    const scopedSubscriptions = scoped.sfid === accountSfid ? scoped.list : null;
    const userReportSubscriptionList = useMemo(() => {
        const activeSfid = accountSfid || userAccountSfid;
        const source = scopedSubscriptions ?? allReportSubscriptions;
        // Salesforce ids come in 15- and 18-char forms; compare the shared 15-char prefix so a
        // format mismatch between the route and the API response doesn't drop every entry.
        const sameAccount = (a, b) => String(a ?? '').slice(0, 15) === String(b ?? '').slice(0, 15);
        return source?.filter((s) => !s.account_sfid || sameAccount(s.account_sfid, activeSfid));
    }, [scopedSubscriptions, allReportSubscriptions, accountSfid, userAccountSfid]);

    const [sendAllTimeCashBackTransactionsReport] = useSendAllTimeCashBackTransactionsReportMutation();
    const [sendClaimedCashBackReport] = useSendClaimedCashBackReportMutation();
    const [sendPropertySummaryReport] = useSendPropertySummaryReportMutation();
    const [subscribeReport] = useSubscribeReportMutation();
    const [fetchTeamMemberInfo] = useLazyGetTeamMemberInfoQuery();

    // Re-read session_info for the chosen account and keep its subscription list locally.
    const loadSubscriptions = useCallback(async () => {
        if (!accountSfid) return false;
        try {
            // Plain `session_info/` (no account_sfid param) — the scoped variant returns the same
            // data, so it isn't called. Filtering to the selected account happens in the memo above.
            const res = await fetchTeamMemberInfo().unwrap();
            setScoped({sfid: accountSfid, list: res?.report_subscription_list ?? []});
            return true;
        } catch {
            return false;
        }
    }, [accountSfid, fetchTeamMemberInfo]);

    // On mount and on every dropdown change.
    useEffect(() => {
        loadSubscriptions();
    }, [loadSubscriptions]);

    // subscribeReport only invalidates the Session/USER tag — the checkbox's real source of
    // truth (userReportSubscriptionList) doesn't update until getTeamMemberInfo's refetch
    // resolves, which happens well after the subscribeReport POST itself resolves. So
    // `pendingToggles` reflects the click immediately, and MUST stay in place until the
    // effect below observes the refetched list actually agreeing with it — clearing it any
    // earlier (e.g. as soon as the subscribeReport call resolves) snaps the checkbox back to
    // the still-stale server value for a moment, then flips again once the refetch lands.
    const [pendingToggles, setPendingToggles] = useState({});
    const toggleKey = (reportId, frequency) => `${reportId}:${frequency}`;

    // Optimistic toggles belong to one account; drop them when the dropdown selection changes.
    useEffect(() => {
        setPendingToggles({});
    }, [accountSfid]);

    useEffect(() => {
        setPendingToggles((prev) => {
            const next = {...prev};
            let changed = false;
            Object.keys(next).forEach((key) => {
                const [reportId, frequency] = key.split(':');
                const entry = userReportSubscriptionList?.find((s) => s.report_id === reportId);
                const serverValue = frequency === 'Weekly' ? entry?.is_weekly ?? false : entry?.is_monthly ?? false;
                if (serverValue === next[key]) {
                    delete next[key];
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [userReportSubscriptionList]);

    const senders = {
        all_time_cash_back_transaction_report: sendAllTimeCashBackTransactionsReport,
        claimed_cash_back_report: sendClaimedCashBackReport,
        property_summary_report: sendPropertySummaryReport
    };

    const getSubscribePayload = useCallback(
        ({report_id, ...payload}) => ({
            user_email: userEmail,
            user_id: userAccountId,
            // Selected account from the route (account dropdown), not the logged-in user's own.
            account_sfid: accountSfid || userAccountSfid,
            report_id,
            ...payload
        }),
        [userEmail, accountSfid, userAccountSfid, userAccountId]
    );

    // `session_info` doesn't reliably return a subscription right after it is saved, so the last
    // toggle the API accepted for each account/report/frequency is also kept in localStorage and
    // layered over the server list. Order of precedence: in-flight click > saved toggle > API.
    const storageKey = `lc_report_subs:${userAccountId}`;
    const savedKey = (reportId, frequency) =>
        `${String(accountSfid || userAccountSfid || '').slice(0, 15)}:${reportId}:${frequency}`;
    const [savedToggles, setSavedToggles] = useState({});
    useEffect(() => {
        try {
            setSavedToggles(JSON.parse(localStorage.getItem(storageKey)) || {});
        } catch {
            setSavedToggles({});
        }
    }, [storageKey]);

    const rows = useMemo(
        () =>
            REPORT_DEFS.map((def) => {
                const entry = userReportSubscriptionList?.find((s) => s.report_id === def.report_id);
                const pick = (frequency, serverValue) => {
                    const pending = pendingToggles[toggleKey(def.report_id, frequency)];
                    if (pending !== undefined) return pending;
                    const saved = savedToggles[savedKey(def.report_id, frequency)];
                    return saved !== undefined ? saved : serverValue;
                };
                return {
                    ...def,
                    isSubscribedWeekly: pick('Weekly', entry?.is_weekly ?? false),
                    isSubscribedMonthly: pick('Monthly', entry?.is_monthly ?? false)
                };
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [userReportSubscriptionList, pendingToggles, savedToggles, accountSfid, userAccountSfid]
    );

    // Toast from Figma node 18778:6630 — the report is emailed rather than downloaded, so the
    // confirmation says it's being processed.
    const [toast, setToast] = useState(null);
    const closeToast = useCallback(() => setToast(null), []);

    const handleAction = async (reportId) => {
        try {
            await senders[reportId]({
                user_email: userEmail,
                ...(communitySfid ? {building_sfid: communitySfid} : {account_sfid: accountSfid})
            }).unwrap();
            setToast({title: 'Report exported', description: 'Your report is being processed and will be emailed to you shortly.', variant: 'success'});
        } catch {
            setToast({title: 'Export failed', description: 'Unable to export the report. Please try again.', variant: 'error'});
        }
    };

    const handleSubscribe = async (def, frequency, checked) => {
        const key = toggleKey(def.report_id, frequency);
        setPendingToggles((prev) => ({...prev, [key]: checked}));
        try {
            // Send only what the API needs. Don't spread `def` (the table row): it carries the
            // row's stale isSubscribedWeekly/Monthly values, which the backend doesn't use.
            await subscribeReport(
                getSubscribePayload({
                    report_id: def.report_id,
                    report_name: def.report_name,
                    is_subscribed: checked,
                    frequency
                })
            ).unwrap();
            // The API accepted it: remember the toggle for this account and drop the in-flight
            // marker. No session_info reload here — it would only replace the tick with a list
            // that may not contain the new subscription yet.
            setSavedToggles((prev) => {
                const next = {...prev, [savedKey(def.report_id, frequency)]: checked};
                try {
                    localStorage.setItem(storageKey, JSON.stringify(next));
                } catch {
                    // storage unavailable — the tick still shows for this session
                }
                return next;
            });
            setPendingToggles((prev) => {
                const next = {...prev};
                delete next[key];
                return next;
            });
        } catch {
            // The write itself failed — revert immediately rather than leaving an optimistic
            // value that the server will never confirm.
            setPendingToggles((prev) => {
                const next = {...prev};
                delete next[key];
                return next;
            });
        }
    };

    const columns = useMemo(
        () => [
            {field: 'report_name', headerName: 'Name', width: 280},
            {
                field: 'weekly_sub',
                headerName: 'Weekly Subscription (every Monday)',
                width: 220,
                renderCell: (row) => (
                    <Checkbox
                        size="small"
                        checked={row.isSubscribedWeekly}
                        onChange={(e) => handleSubscribe(row, 'Weekly', e.target.checked)}
                        sx={{p: 0}}
                    />
                )
            },
            {
                field: 'monthly_sub',
                headerName: 'Monthly Subscription (1st of month)',
                width: 220,
                renderCell: (row) => (
                    <Checkbox
                        size="small"
                        checked={row.isSubscribedMonthly}
                        onChange={(e) => handleSubscribe(row, 'Monthly', e.target.checked)}
                        sx={{p: 0}}
                    />
                )
            },
            {
                field: 'action',
                headerName: '',
                width: 100,
                renderCell: (row) => (
                    <ButtonBase
                        onClick={() => handleAction(row.report_id)}
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {backgroundColor: tokens.color.neutral100}
                        }}
                    >
                        <Box component="img" src={downloadIcon} alt="Export" sx={{width: 16, height: 16}} />
                    </ButtonBase>
                )
            }
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [communitySfid, accountSfid, userEmail, userAccountId, userAccountSfid]
    );

    return (
        <>
            <DataTableShell
                columns={columns}
                rows={rows}
                getRowId={(row) => row.report_id}
                searchPlaceholder="Search reports..."
                showFilterLabel
                showGridLines
                noSortFields={['action']}
                showFooter={false}
            />
            <AuthToast toast={toast} onClose={closeToast} />
        </>
    );
};

const ReportsNotAvailable = () => (
    <Typography variant="body2" color="text.secondary">
        Reports aren&apos;t available for this account.
    </Typography>
);

const GatedReportsSection = () => {
    const params = useParams();
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const userRole = useSelector(getTeamMemberRole);

    // TEMP: gate bypassed for local UI review — restore `if (!canViewReports(...))` below
    // before merging.
    if (!true && !canViewReports(isStakeAdmin, params?.account_sfid, userRole)) {
        return <ReportsNotAvailable />;
    }

    return <ReportsSection />;
}


export default GatedReportsSection;
